import SamIcon from '@mui/icons-material/AutoAwesome';
import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocalStorage } from '@shared/utils/useStorage';
import { ReactNode, useState } from 'react';
import { useTexts } from '@/feature/lang';
import { Icon } from '@/icon/Icon';
import Tooltip from '../Tooltip';
import { chatbotStyles } from './styles';

const ButtonContainer = styled('div', {
  name: 'Chatbot',
  slot: 'ButtonContainer',
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: isCollapsed ? 0 : theme.spacing(4),
  zIndex: theme.zIndex.speedDial,
  transition: theme.transitions.create('right', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
}));

const EntryPointButton = styled(IconButton, {
  name: 'Chatbot',
  slot: 'EntryPointButton',
  shouldForwardProp: (prop) => prop !== 'isCollapsed' && prop !== 'isHovered',
})<{ isCollapsed: boolean; isHovered: boolean }>(
  ({ theme, isCollapsed, isHovered }) => ({
    boxShadow: theme.shadows[6],
    ...chatbotStyles.button(theme),
    height: 50,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),

    ...(isCollapsed && {
      borderRadius: '50% 0 0 50%',
      borderRight: `0px solid ${theme.palette.secondary.main}`,
    }),
    ...(isCollapsed &&
      !isHovered && {
        width: 8,
        borderRadius: '8px 0 0 8px',
        padding: 0,
        minWidth: 8,
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.secondary.main}`,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
        },
        '& .MuiSvgIcon-root': {
          display: 'none',
        },
      }),
  })
);

const ChevronButtonContainer = styled('div', {
  name: 'Chatbot',
  slot: 'ChevronButtonContainer',
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: isCollapsed ? 41 : -9,
  width: 50,
  height: 50,
  padding: 9,
  zIndex: theme.zIndex.speedDial,
  transition: theme.transitions.create('right', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
}));

const ChevronButton = styled(IconButton, {
  name: 'Chatbot',
  slot: 'ChevronButton',
})({
  width: 32,
  height: 32,
});

interface ChatbotButtonProps {
  onClick: () => void;
}

export const ChatbotButton = ({ onClick }: ChatbotButtonProps) => {
  const texts = useTexts().component.chatbot.chatbotButton;
  const [isHovered, setIsHovered] = useState(false);
  const [isCollapsed, setIsCollapsed] = useLocalStorage(
    'chatbot.button.isCollapsed',
    false
  );

  return (
    <>
      <ButtonContainer
        isCollapsed={isCollapsed}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Tooltip title={texts.tooltip} placement="top">
          <EntryPointButton
            onClick={onClick}
            aria-label="open conversation"
            size="large"
            isCollapsed={isCollapsed}
            isHovered={isHovered}
          >
            <SamIcon sx={{ fontSize: 24 }} />
          </EntryPointButton>
        </Tooltip>
      </ButtonContainer>
      {isHovered && (
        <CollapseExpandButton
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          setIsHovered={setIsHovered}
        >
          <Icon
            name={isCollapsed ? 'chevron_left' : 'chevron_right'}
            size="small"
          />
        </CollapseExpandButton>
      )}
    </>
  );
};

interface CollapseExpandButtonProps {
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  setIsHovered: (value: boolean) => void;
  children: ReactNode;
}

const CollapseExpandButton = ({
  isCollapsed,
  setIsCollapsed,
  setIsHovered,
  children,
}: CollapseExpandButtonProps) => {
  return (
    <ChevronButtonContainer
      isCollapsed={isCollapsed}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ChevronButton
        onClick={() => setIsCollapsed(!isCollapsed)}
        size="small"
        aria-label={
          isCollapsed ? 'expand chatbot button' : 'collapse chatbot button'
        }
      >
        {children}
      </ChevronButton>
    </ChevronButtonContainer>
  );
};
